import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import type { HomeworkStatus, SubmissionStatus } from '../types';

const HOMEWORK_VARIANTS: Record<HomeworkStatus, 'default' | 'secondary' | 'outline'> = {
  published: 'default',
  draft: 'outline',
  archived: 'secondary',
};

/** Homework status chip — staff surfaces only (pupil payloads carry no status). */
export function HomeworkStatusBadge({ status }: { status: HomeworkStatus }) {
  const { t } = useTranslation(['homework']);
  return <Badge variant={HOMEWORK_VARIANTS[status]}>{t(`homework:status.${status}`)}</Badge>;
}

const SUBMISSION_VARIANTS: Record<
  SubmissionStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  draft: 'outline',
  submitted: 'secondary',
  returned: 'destructive',
  graded: 'default',
};

/** Submission status chip; `status = undefined` means nothing was started yet. */
export function SubmissionStatusBadge({ status }: { status?: SubmissionStatus }) {
  const { t } = useTranslation(['homework']);
  if (!status) return <Badge variant="outline">{t('homework:submissionStatus.assigned')}</Badge>;
  return (
    <Badge variant={SUBMISSION_VARIANTS[status]}>{t(`homework:submissionStatus.${status}`)}</Badge>
  );
}

/** Sticky late marker — computed once server-side at the first hand-in. */
export function LateBadge() {
  const { t } = useTranslation(['homework']);
  return <Badge variant="destructive">{t('homework:late')}</Badge>;
}
